import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Camera } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { useSession } from '@/hooks/useSession';
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogBody, DialogFooter, DialogClose } from '@/components/ui/Dialog';
import { Button, type ButtonProps } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Label } from '@/components/ui/Label';
import { Avatar } from '@/components/ui/Avatar';
import { AvatarCropperDialog } from '@/components/auth/AvatarCropperDialog';
import { isAdminApiConfigured, deleteImage, extractCfImageId } from '@/lib/adminApi';

// Schema for the editable profile fields. handle is intentionally NOT editable
// here (URL-significant, would break links if changed casually). Admins can
// rename a handle via SQL until we ship a dedicated flow.
const profileSchema = z.object({
  display_name: z.string().trim().min(1, 'Required').max(60),
  avatar_url:   z.string().trim().url('Must be a URL').or(z.literal('')),
  bio:          z.string().trim().max(280).optional(),
});

type ProfileForm = z.infer<typeof profileSchema>;

interface ProfileEditDialogProps {
  /** Render-prop trigger. If omitted, the dialog is controlled-only. */
  trigger?: React.ReactNode;
  triggerProps?: ButtonProps;
}

export function ProfileEditDialog({ trigger }: ProfileEditDialogProps) {
  const { profile } = useSession();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [cropperOpen, setCropperOpen] = useState(false);

  const form = useForm<ProfileForm>({
    defaultValues: {
      display_name: profile?.display_name ?? '',
      avatar_url:   profile?.avatar_url   ?? '',
      bio:          profile?.bio          ?? '',
    },
    values: {
      // re-sync if profile changes (e.g. opened twice across logins)
      display_name: profile?.display_name ?? '',
      avatar_url:   profile?.avatar_url   ?? '',
      bio:          profile?.bio          ?? '',
    },
  });

  const mutation = useMutation({
    mutationFn: async (data: ProfileForm) => {
      if (!profile) throw new Error('No profile loaded');
      const parsed = profileSchema.parse(data);
      const previousAvatarUrl = profile.avatar_url;
      const nextAvatarUrl     = parsed.avatar_url || profile.avatar_url;
      const { error } = await supabase
        .from('users')
        .update({
          display_name: parsed.display_name,
          avatar_url:   nextAvatarUrl, // keep prev if cleared
          bio:          parsed.bio || null,
        })
        .eq('id', profile.id);
      if (error) throw error;

      // Best-effort cleanup of the orphaned previous avatar. Only fires if
      // the avatar actually changed AND it was a CF Images URL we own. A
      // failed delete just leaves a tiny CF Images orphan; not worth
      // surfacing to the user.
      if (previousAvatarUrl !== nextAvatarUrl && isAdminApiConfigured()) {
        const oldId = extractCfImageId(previousAvatarUrl);
        if (oldId) {
          try { await deleteImage(oldId); }
          catch (e) { console.warn('[avatar cleanup] failed to delete orphan', oldId, e); }
        }
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['profile', profile?.id] });
      qc.invalidateQueries({ queryKey: ['users'] });
      toast.success('Profile saved');
      setOpen(false);
    },
  });

  const onSubmit = form.handleSubmit((data) => mutation.mutate(data));
  const avatarPreview = form.watch('avatar_url') || profile?.avatar_url;

  return (
    <>
    <Dialog open={open} onOpenChange={setOpen}>
      {trigger ? <DialogTrigger asChild>{trigger}</DialogTrigger> : null}
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit profile</DialogTitle>
          <DialogDescription>Name, avatar, bio.</DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit}>
          <DialogBody className="space-y-4">
            <div className="flex items-start gap-4">
              <button
                type="button"
                onClick={() => isAdminApiConfigured() && setCropperOpen(true)}
                disabled={!isAdminApiConfigured()}
                className="relative group shrink-0 disabled:cursor-not-allowed"
                title={isAdminApiConfigured() ? 'Change avatar' : 'Admin worker not configured / paste a URL below instead'}
                aria-label="Change avatar"
              >
                <Avatar src={avatarPreview} size={72} />
                <span className="absolute inset-0 rounded-full flex items-center justify-center bg-bg/70 opacity-0 group-hover:opacity-100 group-disabled:opacity-0 transition-opacity">
                  <Camera className="h-5 w-5 text-fg" />
                </span>
              </button>
              <div className="flex-1 space-y-1 min-w-0">
                <Label htmlFor="avatar_url">Avatar URL</Label>
                <Input id="avatar_url" placeholder="https://…" {...form.register('avatar_url')} />
                <p className="text-[11px] text-fg-faint">
                  Click the avatar to upload + crop, or paste a URL.
                </p>
                {form.formState.errors.avatar_url ? <FieldError msg={form.formState.errors.avatar_url.message} /> : null}
              </div>
            </div>

            <div className="space-y-1">
              <Label htmlFor="display_name">Display name</Label>
              <Input id="display_name" autoFocus {...form.register('display_name')} />
              {form.formState.errors.display_name ? <FieldError msg={form.formState.errors.display_name.message} /> : null}
            </div>

            <div className="space-y-1">
              <Label htmlFor="bio">Bio</Label>
              <Textarea id="bio" placeholder="Optional / short blurb (≤ 280 chars)" rows={3} {...form.register('bio')} />
              {form.formState.errors.bio ? <FieldError msg={form.formState.errors.bio.message} /> : null}
            </div>

            <ReadOnlyField label="Handle"     value={profile ? `@${profile.handle}` : '/'} />
            <ReadOnlyField label="Email"      value={profile?.email ?? '/'} />
            <ReadOnlyField label="Role"       value={profile?.role ?? '/'} />

            {mutation.error ? (
              <div className="rounded-md border border-thermal-red/30 bg-thermal-red/5 p-2 text-xs text-thermal-red">
                {(mutation.error as Error).message}
              </div>
            ) : null}
          </DialogBody>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost" type="button">Cancel</Button>
            </DialogClose>
            <Button type="submit" disabled={mutation.isPending || !form.formState.isDirty}>
              {mutation.isPending ? 'Saving…' : 'Save changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>

    <AvatarCropperDialog
      open={cropperOpen}
      onOpenChange={setCropperOpen}
      onUploaded={(url) => form.setValue('avatar_url', url, { shouldDirty: true })}
    />
    </>
  );
}

function FieldError({ msg }: { msg: string | undefined }) {
  if (!msg) return null;
  return <p className="text-xs text-thermal-red mt-1">{msg}</p>;
}

function ReadOnlyField({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between border-t border-border pt-3 text-sm">
      <Label>{label}</Label>
      <span className="font-mono text-xs text-fg-muted">{value}</span>
    </div>
  );
}
